/**
 * The Atlas's resolution governor: the stage's device-pixel ratio under a pixel
 * budget, and a render scale stepped down on slow frames and back up on quick
 * ones, read only off drawn frames in which nothing arrived.
 */
import type * as THREE from 'three';

import { analytics } from '../visits/bus.ts';

export interface FrameGovernorDeps {
  /** The pane: its CSS size is what the pixel budget is spent over. */
  readonly root: HTMLElement;
  /** Read after each draw for the geometries, textures and programs that arrived. */
  readonly renderer: THREE.WebGLRenderer;
  /** Re-sizes the stage at the new ratio; every scale step goes through it. */
  resize(): void;
}

/** What `scene.userData.governor` carries for the descent dojo and the leak check. */
export interface GovernorReading {
  renderScale: number;
  wantPixelRatio: number;
  drawnPixelRatio: number;
  cadenceMs: number;
  emaMs: number;
  clean: number;
  log: Array<{ t: number; from: number; to: number; reason: string; emaMs: number; cadenceMs: number }>;
}

export interface FrameGovernorHandle {
  readonly governor: GovernorReading;
  markLoad(): void;
  loadStarted(): void;
  loadFinished(): void;
  setRenderScale(next: number, reason: string): void;
  pixelRatio(): number;
  basePixelRatio(): number;
  /** One drawn frame's arrivals and timing: after the draw, before `stage.end()`. */
  sample(dt: number, animating: boolean): void;
  /** A fixed device-pixel ratio, or 0 for the governor's own. */
  ssOverride: number;
}

export function createFrameGovernor(deps: FrameGovernorDeps): FrameGovernorHandle {
  const { root, renderer, resize } = deps;

  /**
   * Budget total framebuffer PIXELS, not devicePixelRatio.
   *
   * `setPixelRatio(min(dpr, 2))` on a Retina panel puts 6.2 MP through a shader
   * that marches the height field per fragment — about 18 billion dependent
   * texture reads a second, which is audible as fan noise. This is the same
   * lesson REGOLITH calls "the single most important number here", written into
   * atlas-01 §3.5 and then applied to the preview page and not to the Atlas.
   */
  const PIXEL_BUDGET = 2.4e6;
  /**
   * The resolution governor samples only frames in which nothing arrived.
   *
   * It used to average every frame, so the T0 texture decodes drove it to its
   * 0.6 floor within seconds of opening the tab and further loads kept it
   * there: a 16-draw-call heliocentric view rendered at 60% resolution
   * permanently. A load is the sim's cost, not the GPU's, so a frame that
   * decoded, uploaded or compiled anything is dropped from the average
   * entirely, not counted slow and not counted fast, and a step needs
   * CLEAN_SAMPLES consecutive clean frames behind it.
   *
   * The thresholds are the doc's 24 ms and 13 ms floors, widened by the
   * measured idle cadence so a slower panel still gets to recover: a fixed
   * 13 ms recovery threshold can never be met on a 60 Hz display, where rAF
   * hands back 16.7 ms whatever the GPU is doing, and the governor could only
   * ever step down. The cadence multiplier keeps a 60 Hz+ panel from stepping
   * down until frames are actually dropping (about 4 Hz of headroom lost),
   * not merely slower than 144 Hz.
   */
  const SLOW_FLOOR_MS = 24;
  const SLOW_CADENCE_MULT = 1.5;
  const RECOVER_FLOOR_MS = 13;
  const RECOVER_CADENCE_MULT = 1.15;
  /**
   * Recovery is also allowed against the frame time the scene settled at
   * BEFORE it stepped down.
   *
   * A p10 sits below the interval a vsynced rAF actually hands back - 12.2 ms
   * against a 16.7 ms p50 on the headless compositor, because the left tail
   * widens exactly when frames are being dropped - so a cadence-derived
   * threshold alone is unreachable for the ema, which tracks a mean. One load
   * spike then cost the Atlas 30% of its resolution for the rest of the
   * session; `tools/preview/descent-dojo.mjs` is what caught it.
   */
  const RECOVER_STEADY_MULT = 1.1;
  /** Kept clear of the slow threshold, or the two rules trade steps every second. */
  const RECOVER_HEADROOM = 0.9;
  /** A garbage cadence sample (near-zero dt) must not collapse below a real frame. */
  const CADENCE_FLOOR_MS = 4;
  const CLEAN_SAMPLES = 30;
  const CADENCE_WINDOW = 90;
  const GOV_LOG_MAX = 64;
  let renderScale = 1;
  analytics.renderScale(renderScale);
  let frameEma = 1 / 60;
  /** The last ema read at full scale that was not slow: the pre-spike baseline. */
  let steadyMs = 0;
  let govAccum = 0;
  let cleanRun = 0;
  /** Set by anything that decodes, uploads or compiles; read once per frame. */
  let frameDirty = true;
  /**
   * A render's own upload/compile cost lands in the NEXT frame's dt, not this
   * one's -- this holds that verdict until the tick that should see it.
   */
  let dirtyNext = false;
  let pendingLoads = 0;
  let lastGeometries = -1;
  let lastTextures = -1;
  let lastPrograms = -1;
  const cadence: number[] = [];
  /**
   * Hung off `scene.userData` on purpose: the descent dojo reaches the Atlas
   * scene through `__THREE_DEVTOOLS__` observe events, so the log needs no
   * global and nothing in the app has to know the instrument exists.
   */
  const governor = {
    renderScale: 1,
    /**
     * What `pixelRatio()` asked the stage for, and what the renderer actually
     * carried on the Atlas's last drawn frame.
     *
     * The renderer is the sim's: `src/main.ts` calls `activeStage().release()`
     * before every sim frame, which hands the drawing buffer back at the sim's
     * own ratio, so a read of `renderer.getPixelRatio()` from between frames is
     * the SIM's ratio and says nothing about what the Atlas drew with. These
     * two are the pair `descent-dojo --ship` cross-checks `renderScale` against.
     */
    wantPixelRatio: 1,
    drawnPixelRatio: 1,
    cadenceMs: 0,
    emaMs: 0,
    clean: 0,
    log: [] as Array<{
      t: number; from: number; to: number; reason: string; emaMs: number; cadenceMs: number;
    }>,
  };
  /** Anything whose arrival costs a frame marks it, so the average skips it. */
  function markLoad(): void {
    frameDirty = true;
  }
  function loadStarted(): void {
    pendingLoads++;
    markLoad();
  }
  /** A finished batch hands the governor a clean slate to re-converge from. */
  function loadFinished(): void {
    pendingLoads = Math.max(0, pendingLoads - 1);
    markLoad();
    if (pendingLoads === 0) setRenderScale(1, 'load batch complete');
  }
  function setRenderScale(next: number, reason: string): void {
    const from = renderScale;
    cleanRun = 0;
    govAccum = 0;
    if (Math.abs(next - from) < 0.005) return;
    renderScale = next;
    governor.renderScale = next;
    governor.log.push({
      t: Date.now(), from: +from.toFixed(3), to: +next.toFixed(3), reason,
      emaMs: +(frameEma * 1000).toFixed(2), cadenceMs: +governor.cadenceMs.toFixed(2),
    });
    if (governor.log.length > GOV_LOG_MAX) governor.log.shift();
    analytics.renderScale(next);
    resize();
  }
  /**
   * A fixed device-pixel ratio, for the seam and aliasing instruments.
   *
   * Bypasses the pixel budget AND the governor: the point of the measurement is
   * a 4x render of the same scene, and a governor that reacts to how long that
   * takes would give back a different image instead of a supersampled one.
   */
  let ssOverride = 0;
  function pixelRatio(): number {
    if (ssOverride > 0) return ssOverride;
    return basePixelRatio();
  }
  function basePixelRatio(): number {
    const w = Math.max(1, root.clientWidth);
    const h = Math.max(1, root.clientHeight);
    let px = Math.min(window.devicePixelRatio || 1, 2);
    const over = (w * h * px * px) / PIXEL_BUDGET;
    if (over > 1) px /= Math.sqrt(over);
    return Math.max(0.5, px * renderScale);
  }
  function sample(dt: number, animating: boolean): void {
    // Anything that arrived this frame, whether or not it announced itself:
    // a rising geometry, texture or program count is a decode, an upload or
    // a compile, which is the sim's cost and not the GPU's. That cost lands
    // in the render just performed, which predates this tick's dt sample --
    // it is the NEXT tick's dt that will carry it, so it marks dirtyNext,
    // not frameDirty.
    const mem = renderer.info.memory;
    const progs = renderer.info.programs?.length ?? 0;
    if (lastGeometries >= 0 && (mem.geometries > lastGeometries
      || mem.textures > lastTextures || progs > lastPrograms)) dirtyNext = true;
    lastGeometries = mem.geometries;
    lastTextures = mem.textures;
    lastPrograms = progs;

    // Resolution governor: trade pixels for a steady frame before the player
    // notices, and give them back when there is headroom. Only meaningful
    // while actually animating, since an idle frame's timing says nothing,
    // and only on frames in which nothing arrived.
    if (animating && !frameDirty && pendingLoads === 0) {
      frameEma = frameEma * 0.9 + dt * 0.1;
      cadence.push(dt * 1000);
      if (cadence.length > CADENCE_WINDOW) cadence.shift();
      cleanRun++;
      govAccum += dt;
      governor.emaMs = frameEma * 1000;
      governor.clean = cleanRun;
      if (govAccum > 1.0 && cleanRun >= CLEAN_SAMPLES) {
        // Sorted here rather than per frame: this runs once a second at most.
        // p10, not the median: the median of a GPU-bound scene's own frame
        // times drifts above the true vsync interval and drags the slow
        // threshold up with it, so the governor stops stepping down right
        // when it should. p10 sits at or below the vsync floor instead, which
        // is why it is the slow threshold's estimator and not recovery's.
        const sorted = [...cadence].sort((a, b) => a - b);
        const cadenceMs = Math.max(CADENCE_FLOOR_MS, sorted[Math.floor(sorted.length * 0.1)] ?? 1000 / 60);
        governor.cadenceMs = cadenceMs;
        const ms = frameEma * 1000;
        const slowMs = Math.max(SLOW_FLOOR_MS, cadenceMs * SLOW_CADENCE_MULT);
        if (renderScale >= 1 && ms <= slowMs) steadyMs = ms;
        const recoverMs = Math.min(
          slowMs * RECOVER_HEADROOM,
          Math.max(RECOVER_FLOOR_MS, cadenceMs * RECOVER_CADENCE_MULT, steadyMs * RECOVER_STEADY_MULT),
        );
        if (ssOverride > 0) {
          govAccum = 0;
        } else if (ms > slowMs && renderScale > 0.6) {
          setRenderScale(Math.max(0.6, renderScale - 0.1), 'slow');
        } else if (ms < recoverMs && renderScale < 1) {
          setRenderScale(Math.min(1, renderScale + 0.07), 'recover');
        } else {
          govAccum = 0;
        }
      }
    } else if (frameDirty || pendingLoads > 0) {
      cleanRun = 0;
      governor.clean = 0;
    }
    frameDirty = dirtyNext;
    dirtyNext = false;
  }

  return {
    governor, markLoad, loadStarted, loadFinished, setRenderScale, pixelRatio, basePixelRatio, sample,
    get ssOverride() { return ssOverride; },
    set ssOverride(v) { ssOverride = v; },
  };
}
